// Google Ads / gtag.js helper. Safe on SSR & when gtag not loaded (e.g., ad blockers).

export const GOOGLE_ADS_ID = "AW-18326636501";
// Conversion labels — fill these in via `configureConversion` when you receive them
// from your Google Ads Conversion Actions (Format: "AW-18326636501/AbCdEfGhIj-KlMnOp").
const conversionSendTo = {
  quote_submit: null,   // Set when you create a "Offerte aanvraag" conversion in Google Ads
  phone_click: null,    // Set for "Bel direct" clicks
  whatsapp_click: null, // Set for WhatsApp clicks
};

export function configureConversion(kind, sendTo) {
  if (kind in conversionSendTo) conversionSendTo[kind] = sendTo;
}

function safeGtag(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag === "function") {
    try { window.gtag(...args); } catch (_) { /* ignore */ }
  }
}

/**
 * Normalise inputs the way Google Ads Enhanced Conversions expects
 * (lower/trim, phone -> E.164). gtag itself SHA-256 hashes them on send.
 * https://developers.google.com/google-ads/api/docs/conversions/enhanced-conversions
 */
function normalizeEmail(email) {
  if (!email) return undefined;
  return String(email).trim().toLowerCase();
}

function normalizePhoneNL(phone) {
  if (!phone) return undefined;
  const digits = String(phone).replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.startsWith("00")) return "+" + digits.slice(2);
  if (digits.startsWith("0")) return "+31" + digits.slice(1);
  // Bare 9-10 digit assumed NL mobile
  if (/^\d{9,10}$/.test(digits)) return "+31" + digits.replace(/^0/, "");
  return digits;
}

function splitName(full) {
  if (!full) return {};
  const parts = String(full).trim().split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0].toLowerCase() };
  return {
    first_name: parts[0].toLowerCase(),
    last_name: parts.slice(1).join(" ").toLowerCase(),
  };
}

/**
 * Build a user_data object accepted by gtag Enhanced Conversions.
 * Only include fields that are present so we never send empty strings
 * (Google penalizes match rate if you send blanks).
 */
export function buildUserData({ email, phone, name, address, city, region, postal, country = "NL" } = {}) {
  const ud = {};
  const e = normalizeEmail(email);
  const p = normalizePhoneNL(phone);
  if (e) ud.email = e;
  if (p) ud.phone_number = p;
  const nm = splitName(name);
  const addr = {};
  if (nm.first_name) addr.first_name = nm.first_name;
  if (nm.last_name) addr.last_name = nm.last_name;
  if (address) addr.street = String(address).trim().toLowerCase();
  if (city) addr.city = String(city).trim().toLowerCase();
  if (region) addr.region = String(region).trim().toLowerCase();
  if (postal) addr.postal_code = String(postal).trim().toLowerCase();
  if (country) addr.country = String(country).trim().toUpperCase();
  if (Object.keys(addr).length) ud.address = addr;
  return ud;
}

/**
 * Fire a Google Ads conversion event with optional Enhanced Conversions data.
 * @param {string} kind - one of `quote_submit` | `phone_click` | `whatsapp_click`
 * @param {object} params - extra event params (value, currency, work_type, ...)
 * @param {object} userData - raw user data (email, phone, name, address, ...)
 */
export function trackConversion(kind, params = {}, userData = null) {
  const sendTo = conversionSendTo[kind];
  const ud = userData ? buildUserData(userData) : null;

  // Enhanced Conversions: set user_data at page level so subsequent events pick it up.
  if (ud && Object.keys(ud).length) {
    safeGtag("set", "user_data", ud);
  }

  if (sendTo) {
    const eventParams = { send_to: sendTo, ...params };
    if (ud && Object.keys(ud).length) eventParams.user_data = ud;
    safeGtag("event", "conversion", eventParams);
    return;
  }
  // Fallback: Google recommended events. Ads with auto-tagging still sees these
  // as engagement even without a Conversion Action label.
  const eventMap = {
    quote_submit: "generate_lead",
    phone_click: "click_call",
    whatsapp_click: "click_whatsapp",
  };
  const name = eventMap[kind] || kind;
  const eventParams = { ...params };
  if (ud && Object.keys(ud).length) eventParams.user_data = ud;
  safeGtag("event", name, eventParams);
}

/**
 * Fire a synthetic page_view so Google Ads keeps attributing sessions correctly
 * across SPA route + language changes (client-side navigation).
 */
export function trackPageView(pathname) {
  if (typeof window === "undefined") return;
  const path = pathname || window.location.pathname + window.location.search;
  const location = window.location.origin + path;
  safeGtag("event", "page_view", {
    page_location: location,
    page_path: path,
    page_title: document.title,
  });
  // Re-run config so gclid / linker stays fresh
  safeGtag("config", GOOGLE_ADS_ID, {
    page_path: path,
    allow_enhanced_conversions: true,
  });
}

/** Simple page-level engagement event (already covered by gtag config, but usable). */
export function trackPageEvent(eventName, params = {}) {
  safeGtag("event", eventName, params);
}
